import { useState, useEffect } from 'react';

interface ABTestVariant<T> {
  id: string;
  weight: number;
  value: T;
}

interface ABTestConfig<T> {
  testName: string;
  variants: ABTestVariant<T>[];
}

const STORAGE_PREFIX = 'ab_test_';

/**
 * A/B Testing hook that persists variant selection per user
 */
export function useABTest<T>(config: ABTestConfig<T>): { variant: ABTestVariant<T>; trackConversion: () => void } {
  const storageKey = `${STORAGE_PREFIX}${config.testName}`;
  
  const [selectedVariant, setSelectedVariant] = useState<ABTestVariant<T>>(() => {
    // Check if user already has a variant assigned
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);
      const found = config.variants.find(v => v.id === parsed.variantId);
      if (found) return found;
    }
    
    // Assign new variant based on weights
    const totalWeight = config.variants.reduce((sum, v) => sum + v.weight, 0);
    const random = Math.random() * totalWeight;
    let cumulative = 0;
    
    for (const variant of config.variants) {
      cumulative += variant.weight;
      if (random <= cumulative) {
        return variant;
      }
    }
    
    return config.variants[0];
  });

  useEffect(() => {
    // Persist the selected variant
    localStorage.setItem(storageKey, JSON.stringify({
      variantId: selectedVariant.id,
      assignedAt: Date.now(),
    }));
  }, [selectedVariant, storageKey]);

  const trackConversion = () => {
    // Track conversion for analytics
    const conversionKey = `${storageKey}_conversions`;
    const conversions = JSON.parse(localStorage.getItem(conversionKey) || '{}');
    conversions[selectedVariant.id] = (conversions[selectedVariant.id] || 0) + 1;
    localStorage.setItem(conversionKey, JSON.stringify(conversions));
    
    console.log(`[A/B Test] ${config.testName} - Conversion for variant: ${selectedVariant.id}`);
  };

  return { variant: selectedVariant, trackConversion };
}

// Modal A/B Test Configurations
export interface ModalABTestContent {
  headline: string;
  subheadline: string;
  ctaText: string;
}

// Brand-specific decline humor text
export const DECLINE_TEXT_BY_BRAND: Record<'bbq', string> = {
  bbq: "No thanks, discounts make me uncomfortable",
};

export const MODAL_AB_TEST_CONFIG: ABTestConfig<ModalABTestContent> = {
  testName: 'savings_modal_v1',
  variants: [
    {
      id: 'control',
      weight: 50,
      value: {
        headline: 'Want to Save Up to 60%?',
        subheadline: 'Enter your email to unlock exclusive showroom savings not advertised online.',
        ctaText: 'Unlock My Savings',
      },
    },
    {
      id: 'urgency',
      weight: 25,
      value: {
        headline: 'Exclusive: 60% Off Today Only',
        subheadline: 'Join our VIP list for showroom-only deals you won\'t find anywhere else.',
        ctaText: 'Get My VIP Access',
      },
    },
    {
      id: 'fomo',
      weight: 25,
      value: {
        headline: 'You\'re Missing Out on 60% Savings',
        subheadline: 'Over 15,000 smart shoppers have already claimed their exclusive discount.',
        ctaText: 'Claim My Discount',
      },
    },
  ],
};

// Helper to get A/B test stats for dashboard
export const getABTestStats = (testName: string) => {
  const conversionKey = `${STORAGE_PREFIX}${testName}_conversions`;
  const storedConversions = JSON.parse(localStorage.getItem(conversionKey) || '{}');
  
  return {
    conversions: storedConversions,
    testName,
  };
};
